// @file Surface Accounts Store

import initialLibrary, { buildSoloLibrary } from '@@/bits/current_library'
import { captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { FetchJsonStatus, LibraryMembershipRole, LibraryType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { fetchJson } from '@@/surface/api_fetch'
import type { JsonApiData, Library, LibraryId, Tenant, TenantId, User } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const NATIVE_TENANT_ID = 1
const DEFAULT_AVATAR = 'https://padlet.net/avatars/tenant_avatar.webp'

const useSurfaceAccountsStore = defineStore('surfaceAccountsStore', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceStore = useSurfaceStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()

  function genericFetchError(payload: { error: any; source: string }): void {
    captureNonNetworkFetchError(payload.error, { source: payload.source })
    globalSnackbarStore.genericFetchError()
  }

  const currentUser = computed(() => surfaceCurrentUserStore.currentUser)
  const wallId = computed(() => surfaceStore.wallId)

  // #region tenants
  const tenantsById = ref<Record<TenantId, Tenant>>({})
  const currentTenant = computed((): Tenant | null => {
    if (Object.keys(tenantsById.value).length === 0) return null
    if (currentUser.value == null) return null
    return tenantsById.value[currentUser.value.tenant_id]
  })
  const currentTenantName = computed((): string => currentTenant.value?.name ?? '')
  const currentTenantLogo = computed((): string =>
    currentTenant.value?.assets != null ? (currentTenant.value?.assets?.home_logo as string) : DEFAULT_AVATAR,
  )
  const isBackpack = computed((): boolean => {
    if (currentUser.value == null) return false
    return currentTenant.value != null
      ? currentTenant.value.type === 'school'
      : currentUser.value.tenant_type === 'school'
  })
  const isBriefcase = computed((): boolean => currentTenant.value?.type === 'business')

  const isStudent = computed((): boolean => currentUser.value?.role === 'student')
  const isTeacher = computed((): boolean => currentUser.value?.role === 'teacher')
  const isAdmin = computed((): boolean => currentUser.value?.role === 'admin')
  const isOwner = computed((): boolean => currentUser.value?.role === 'owner')
  const isNativeAccount = computed((): boolean => currentUser.value?.tenant_id === NATIVE_TENANT_ID)
  const isNativeAdmin = computed((): boolean => isNativeAccount.value && isAdmin.value)
  // #endregion

  // #region libraries
  const librariesById = ref<Record<LibraryId, Library>>({})

  const libraryId = computed(() => surfaceStore.libraryId)

  const currentLibrary = computed((): Library | null => {
    if (libraryId.value == null) return null
    return librariesById.value[libraryId.value] ?? initialLibrary
  })
  const isCurrentLibrarySchool = computed((): boolean => currentLibrary.value?.libraryType === LibraryType.School)
  const isCurrentLibraryClassroom = computed((): boolean => currentLibrary.value?.libraryType === LibraryType.Classroom)
  const isCurrentLibraryTeam = computed((): boolean => currentLibrary.value?.libraryType === LibraryType.Team)
  const currentLibraryId = computed((): LibraryId | null => currentLibrary.value?.id ?? null)

  // The library membership of the current user in the current library
  const currentLibraryMembership = computed(() => {
    if (currentLibrary.value == null || currentUser.value == null) return null
    if (currentLibrary.value.membership == null) return null
    return currentLibrary.value?.membership.find((m) => m.user_id === currentUser.value?.id) || null
  })
  const isLibraryOwner = computed(() => currentLibraryMembership.value?.role === LibraryMembershipRole.Owner)
  const isLibraryAdmin = computed(() => currentLibraryMembership.value?.role === LibraryMembershipRole.Admin)
  const isLibraryTeacher = computed(() => currentLibraryMembership.value?.role === LibraryMembershipRole.Teacher)

  const isEducationalLibrary = computed(
    (): boolean => surfaceStore.isLibraryWall && (isCurrentLibraryClassroom.value || isCurrentLibrarySchool.value),
  )
  const isLibraryTeacherOrAbove = computed(
    (): boolean =>
      isEducationalLibrary.value && (isLibraryOwner.value || isLibraryAdmin.value || isLibraryTeacher.value),
  )
  const isBackpackTeacherOrAbove = computed(
    (): boolean => isBackpack.value && (isTeacher.value || isAdmin.value || isOwner.value),
  )

  const defaultPersonalLibrary = computed((): Partial<Library> => {
    return buildSoloLibrary(currentUser.value)
  })
  const wallViewableAndVisibleLibrariesArray = computed((): Library[] =>
    Object.values(librariesById.value).filter((library) => library.visible),
  )
  // #endregion

  // #region accounts state fetching
  let fetchAccountsPromise: Promise<void> | null = null
  const fetchAccountsStatus = ref<FetchJsonStatus>(FetchJsonStatus.NotStarted)
  const isAccountsFetched = computed((): boolean => fetchAccountsStatus.value === FetchJsonStatus.Completed)
  const isAccountsFetching = computed((): boolean => fetchAccountsStatus.value === FetchJsonStatus.Fetching)

  async function fetchAccounts(): Promise<void> {
    // If already completed, return immediately
    if (isAccountsFetched.value) {
      return
    }

    // If already fetching, return the existing promise (multiple callers will await the same fetch)
    if (fetchAccountsPromise) {
      return fetchAccountsPromise
    }

    // Start a new fetch and store the promise
    fetchAccountsPromise = (async function performFetch() {
      fetchAccountsStatus.value = FetchJsonStatus.Fetching
      try {
        const response = await fetchJson(`/api/1/accounts`)
        const accountData = response.data as Array<JsonApiData<User | Library>>
        const tenantData = response.included as Array<JsonApiData<Tenant>>

        tenantData.forEach((item) => {
          if (item.type === 'tenant') {
            tenantsById.value[item.id] = item.attributes
          }
        })

        accountData.forEach((account) => {
          if (account.type === 'library') {
            librariesById.value[account.id] = account.attributes
          }
        })

        fetchAccountsStatus.value = FetchJsonStatus.Completed
      } catch (error) {
        genericFetchError({ error, source: 'SurfaceAccountsStoreFetchAccounts' })
        fetchAccountsStatus.value = FetchJsonStatus.Error
      } finally {
        // Clear the promise reference after completion
        fetchAccountsPromise = null
      }
    })()

    return fetchAccountsPromise
  }
  // #endregion

  async function initialize(): Promise<void> {
    if (surfaceCurrentUserStore.currentUser == null) return
    await fetchAccounts()
  }

  return {
    currentUser,
    currentTenant,
    currentTenantName,
    currentTenantLogo,
    wallId,
    libraryId,
    librariesById,
    currentLibrary,
    isCurrentLibrarySchool,
    isCurrentLibraryClassroom,
    isCurrentLibraryTeam,
    currentLibraryId,
    currentLibraryMembership,
    isLibraryOwner,
    isLibraryAdmin,
    isLibraryTeacher,
    isStudent,
    isTeacher,
    isAdmin,
    isOwner,
    isNativeAccount,
    isNativeAdmin,
    isBackpack,
    isBriefcase,
    isEducationalLibrary,
    isLibraryTeacherOrAbove,
    isBackpackTeacherOrAbove,
    defaultPersonalLibrary,
    wallViewableAndVisibleLibrariesArray,
    isAccountsFetched,
    isAccountsFetching,
    initialize,
  }
})
export { useSurfaceAccountsStore }
